import type { GamePhase, PlayerObservationV1 } from "@fate-kingdom/engine";

import type { AlphaDeckId } from "../alpha-session-controller";

export interface MatchHudModel {
  readonly humanDeckId: AlphaDeckId;
  readonly aiDeckId: AlphaDeckId;
  readonly opponentBanners: number;
  readonly opponentHandCount: number;
  readonly opponentDeckCount: number;
  readonly ownBanners: number;
  readonly ownHandCount: number;
  readonly ownDeckCount: number;
  readonly ownKingdomComplete: boolean;
  readonly opponentKingdomComplete: boolean;
  readonly ownResources: {
    readonly food: number;
    readonly production: number;
    readonly trade: number;
    readonly science: number;
    readonly culture: number;
  };
  readonly turnNumber: number;
  readonly phase: GamePhase;
  readonly phaseSummary: string;
  readonly statusMessage: string;
  readonly aiState: "hidden" | "thinking" | "paused" | "fast";
  readonly phaseActionLabel: string | null;
  readonly phaseActionEnabled: boolean;
  readonly territoryDeadline: boolean;
}

const PHASE_PROMPT: Readonly<Record<GamePhase, string>> = {
  ready: "Ready formations",
  draw: "Draw for the turn",
  land: "Play or develop one Land",
  supply: "Support local Armies",
  command: "Deploy one Commander or Army",
  battle: "Attack with ready formations",
  cleanup: "End the turn",
  "action-window": "Play, attack, or End Turn",
  finished: "Match complete",
};

export function createMatchHudModel({
  observation,
  humanDeckId,
  aiDeckId,
  isAiTurn,
  paused,
  quickAi,
  statusMessage,
  phaseActionLabel,
  phaseActionEnabled,
  territoryDeadline,
}: {
  readonly observation: PlayerObservationV1;
  readonly humanDeckId: AlphaDeckId;
  readonly aiDeckId: AlphaDeckId;
  readonly isAiTurn: boolean;
  readonly paused: boolean;
  readonly quickAi: boolean;
  readonly statusMessage: string;
  readonly phaseActionLabel: string | null;
  readonly phaseActionEnabled: boolean;
  readonly territoryDeadline: boolean;
}): MatchHudModel {
  return {
    humanDeckId,
    aiDeckId,
    opponentBanners: observation.opponent.victoryBanners,
    opponentHandCount: observation.opponent.handCount,
    opponentDeckCount: observation.opponent.deckCount,
    ownBanners: observation.own.victoryBanners,
    ownHandCount: observation.own.hand.length,
    ownDeckCount: observation.own.deckCount,
    ownKingdomComplete: observation.kingdomProgress?.own.complete ?? false,
    opponentKingdomComplete: observation.kingdomProgress?.opponent.complete ?? false,
    ownResources: observation.own.kingdomPool,
    turnNumber: observation.turnNumber,
    phase: observation.phase,
    phaseSummary: territoryDeadline
      ? "Restore Territory · Establish an active Land"
      : PHASE_PROMPT[observation.phase],
    statusMessage,
    aiState: !isAiTurn ? "hidden" : paused ? "paused" : quickAi ? "fast" : "thinking",
    phaseActionLabel,
    phaseActionEnabled,
    territoryDeadline,
  };
}
